import { FormEvent, useEffect, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../../libs/Authentication';
import { Link } from 'react-router-dom';
import { accountInterface } from '../../types/accounts';
import { callCloudFunction, createStripeCustomer } from '../../services/db';
import { authErrorHandler, getRandomPinCode, passwordValid } from '../../libs/functions';
import dayjs from 'dayjs';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import PrimaryInput from '../../components/input/PrimaryInput';
import FeedbackBox from '../../components/FeedbackBox';
//import { prices } from '../../libs/constants';
import { localize as l } from '../../libs/Localization';
import { authentication } from '../../services/firebase';
import PrimaryPasswordInput from '../../components/input/PrimaryPasswordInput';
import NoAuthFormContainer from '../../components/containers/NoAuthFormContainer';
import TermsAndConditions from '../../components/forms/TermsAndConditions';
import FormTopBar from '../../components/forms/FormTopBar';
import FieldHelperPasswordStrength, { IFieldHelperPasswordStrength } from '../../components/onboarding/FieldHelperPasswordStrength';
import { amplitudeTrack } from '../../services/amplitude';

const Register = () => {
	const emailRef = useRef<HTMLInputElement>(null);
	const passwordRef = useRef<HTMLInputElement>(null);
	const nameRef = useRef<HTMLInputElement>(null);
	const schoolRef = useRef<HTMLInputElement>(null);
	const countryRef = useRef<HTMLInputElement>(null);
	const [newsletter, setNewsletter] = useState<boolean>(false);
	const [tracked, setTracked] = useState<boolean>(false);

	// Error state from redirect
	const { referredBy } = useParams();

	//const location = useLocation();
	//const params = new URLSearchParams(location.search);
	//const freeTrial = params.get('freeTrial');

	// ? future? Check if referredBy is a legitimate referrer if not send them to Register
	// ? future? If legitimate add referredBy variable to user document

	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [passwordIsValid, setPasswordIsValid] = useState<boolean>(false);
	const [passwordHelperStatus, setPasswordHelperStatus] = useState<IFieldHelperPasswordStrength>({
		show: false,
		capital: false,
		number: false,
		characters: false,
	});
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const auth = useAuth();

	const createNewSchool = async () => {
		let newUID: string | null = null;
		const schoolEmail = emailRef.current ? emailRef.current?.value.trim() : '';
		const schoolUserName = nameRef.current ? nameRef.current?.value.trim() : '';
		const schoolSchoolName = schoolRef.current ? schoolRef.current?.value.trim() : '';
		const schoolCountry = countryRef.current ? countryRef.current?.value.trim() : '';
		try {
			if (emailRef.current && passwordRef.current && nameRef.current) {
				await authentication
					.createUserWithEmailAndPassword(emailRef.current.value.trim(), passwordRef.current.value)
					.then(async (result) => {
						auth.setUserType && auth.setUserType('school');
						const accountData: accountInterface = {
							accountType: 'school',
							name: schoolUserName,
							school: schoolSchoolName,
							country: schoolCountry,
							email: schoolEmail,
							subscription: { status: 'trial', seats: 25 },
							isSchoolAccount: true,
							newsletterSubscription: newsletter,
							...(referredBy && { referredBy: referredBy }),
							pinCode: getRandomPinCode(),
							created: dayjs().toISOString(),
						};
						if (result.user) {
							createStripeCustomer(result.user?.uid!, schoolEmail);
							const createSchoolAccount = await callCloudFunction('createAccount');
							await createSchoolAccount({ platform: 'web', accountData: accountData }).then(() => {
								window.location.reload();
							});
						}
					})
					.catch((error) => {
						const errorMessage = authErrorHandler(error);
						setError(errorMessage);
					});
			}
		} catch (error) {
			console.error(error, newUID);
			setError(
				l['Error creating account, try again later. If it persists please contact us at schools@mussila.com.']
			);
		}
	};

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		try {
			setError('');
			setLoading(true);
			if (passwordRef.current) {
				if (passwordValid(passwordRef.current?.value)) {
					setLoading(false);
					await createNewSchool();
				}
			}
		} catch (error) {
			setError(l['Email or password incorrect.']);
			setLoading(false);
		}
	};

	useEffect(() => {
		if (!tracked) {
			amplitudeTrack('School Registration Page View');
			setTracked(true);
		}
		return () => {};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	} , [auth.session, auth.type]);

	return auth.session !== null ? (
		<Navigate to='/' />
	) : (
		<NoAuthFormContainer>
			<FormTopBar text='SIGN UP' title={l['School']} />
			<form onSubmit={handleSubmit} className='m-2 flex flex-col'>
				<FeedbackBox error={error} />
				{referredBy ? (
					<PrimaryInput
						label={l['Referred By']}
						type='text'
						name='referredBy'
						disabled={true}
						required={true}
						value={referredBy}
						addMargin={true}
					/>
				) : null}
				<PrimaryInput
					label={l['Your Email']}
					placeholder={l['Enter your email']}
					type='email'
					name='email'
					required={true}
					ref={emailRef}
					addMargin={true}
				/>
				<PrimaryPasswordInput
					label={l['Your Password']}
					placeholder={l['Enter your password']}
					type='password'
					name='password'
					required={true}
					ref={passwordRef}
					validate={(value) => passwordValid(value, setPasswordHelperStatus, setPasswordIsValid)}
					valid={passwordIsValid}
				/>
				<FieldHelperPasswordStrength
					show={passwordHelperStatus.show}
					capital={passwordHelperStatus.capital}
					characters={passwordHelperStatus.characters}
					number={passwordHelperStatus.number}
				/>
				<PrimaryInput
					label={l['School name']}
					placeholder={l["Enter the school's name"]}
					type='text'
					name='school'
					required={true}
					ref={schoolRef}
					addMargin={true}
				/>
				<PrimaryInput
					label={l['Contact name']}
					placeholder={l['Enter your name']}
					type='text'
					name='name'
					required={true}
					ref={nameRef}
					addMargin={true}
				/>
				<PrimaryInput
					label={l['Country']}
					placeholder={l["Enter the school's country"]}
					type='text'
					name='country'
					required={true}
					ref={countryRef}
					addMargin={true}
				/>
				<div className='flex mt-6'>
					<label className='flex items-center'>
						<input
							type='checkbox'
							className='form-checkbox text-orange-400 focus:ring-orange-400'
							name='newsletter'
							checked={newsletter}
							onChange={() => setNewsletter(!newsletter)}
						/>
						<span className='ml-2'>{l['I would like to receive special offers and news']}</span>
					</label>
				</div>
				<PrimaryButton type='submit' disabled={loading}>
					{l['Create account']}
				</PrimaryButton>
			</form>
			<TermsAndConditions />
			<Link to='/login'>
				<div className='text-center text-gray-600 text-sm mt-4 hover:text-gray-500'>
					{l['Already have an account?']}
				</div>
			</Link>
		</NoAuthFormContainer>
	);
};

export default Register;
